// src/App.jsx
import { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'swiper/css';
import 'swiper/css/navigation';
import 'lightgallery/css/lightgallery.css';
import 'lightgallery/css/lg-thumbnail.css';
import 'lightgallery/css/lg-zoom.css';
import '@fortawesome/fontawesome-free/css/all.min.css';
import './App.css';

import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ScrollToTop from './components/ScrollToTop';
import SEO from './components/SEO';
import Loader from './components/Loader';
import { SiteDataProvider } from './context/SiteDataContext';
import ProtectedRoute from './components/admin/ProtectedRoute';
import AdminLogin from './components/admin/AdminLogin';
import AdminPanel from './components/admin/AdminPanel';

// Eager — home page sections
import Header from './components/Header';
import About from './components/About';
import Services from './components/Services';
import Team from './components/Team';
import Contact from './components/Contact';
import Gallery from './components/Gallery';

// Lazy — service + other route pages
const Fizioterapija            = lazy(() => import('./components/Fizioterapija'));
const Ortopedija               = lazy(() => import('./components/Ortopedija'));
const Kineziterapija           = lazy(() => import('./components/Kineziterapija'));
const Blog2                    = lazy(() => import('./components/Blog2'));
const FizikalniPregled         = lazy(() => import('./components/FizikalniPregled'));
const UltrazvucnaDijagnostika  = lazy(() => import('./components/UltrazvucnaDijagnostika'));
const EmmettTerapija           = lazy(() => import('./components/EmmetTerapija'));
const BowenTerapija            = lazy(() => import('./components/BowenTerapija'));
const Hijaluron                = lazy(() => import('./components/Hijaluron'));
const Blokada                  = lazy(() => import('./components/Blokada'));
const UdarniVal                = lazy(() => import('./components/UdarniVal'));
const DryNeedling              = lazy(() => import('./components/DryNeedling'));
const LimfnaDrenaza            = lazy(() => import('./components/LimfnaDrenaza'));
const PolitikaPrivatnosti      = lazy(() => import('./components/PolitikaPrivatnosti'));
const PravilaKolacici          = lazy(() => import('./components/PravilaKolacici'));
const Cjenik                   = lazy(() => import('./components/Cjenik'));
const TeamMemberDetail         = lazy(() => import('./components/Dejan'));

const organizationSchema = {
  "@context": "https://schema.org",
  "@type": "MedicalClinic",
  "name": "Poliklinika Akrion",
  "description": "Stručna fizioterapija, ortopedija i kineziterapija u Labinu",
  "url": "https://poliklinika-akrion.hr",
  "telephone": "+385916213121",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "Rudarska 8",
    "addressLocality": "Labin",
    "postalCode": "52220",
    "addressCountry": "HR"
  },
  "openingHoursSpecification": [{
    "@type": "OpeningHoursSpecification",
    "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
    "opens": "09:00",
    "closes": "20:00"
  }],
  "medicalSpecialty": ["Fizioterapija", "Ortopedija", "Kineziterapija", "Ultrazvučna dijagnostika"],
  "priceRange": "$$"
};

// Layout wrapper for public pages (Navbar + Footer)
function PublicLayout({ children }) {
  return (
    <>
      <Navbar />
      {children}
      <Footer />
    </>
  );
}

function App() {
  const [loading, setLoading] = useState(() => !sessionStorage.getItem('appLoaded'));

  useEffect(() => {
    if (sessionStorage.getItem('appLoaded')) return;

    const fallback = setTimeout(() => {
      setLoading(false);
      sessionStorage.setItem('appLoaded', 'true');
    }, 3000);

    const img = new Image();
    img.onload = img.onerror = () => {
      setTimeout(() => {
        setLoading(false);
        sessionStorage.setItem('appLoaded', 'true');
      }, 300);
    };
    img.src = '/assets/akrion-07.webp';

    return () => clearTimeout(fallback);
  }, []);

  if (loading) return <Loader />;

  return (
    <HelmetProvider>
      <Router>
        <ScrollToTop />
        <Routes>

          {/* ── Admin routes (no Navbar/Footer, no SiteDataProvider) ── */}
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute>
                <AdminPanel />
              </ProtectedRoute>
            }
          />

          {/* ── Public routes ── */}
          <Route
            path="/*"
            element={
              <SiteDataProvider>
                <PublicLayout>
                  <Suspense fallback={<Loader />}>
                    <Routes>
                      <Route
                        path="/"
                        element={
                          <>
                            <SEO structuredData={organizationSchema} />
                            <Header />
                            <About />
                            <Services />
                            <Team />
                            <Contact />
                            <Gallery />
                          </>
                        }
                      />
                      <Route path="/fizioterapija"        element={<Fizioterapija />} />
                      <Route path="/fizikalni-pregled"    element={<FizikalniPregled />} />
                      <Route path="/ortopedija"           element={<Ortopedija />} />
                      <Route path="/kineziterapija"       element={<Kineziterapija />} />
                      <Route path="/ultrazvuk"            element={<UltrazvucnaDijagnostika />} />
                      <Route path="/emmett-terapija"      element={<EmmettTerapija />} />
                      <Route path="/bowen-terapija"       element={<BowenTerapija />} />
                      <Route path="/hijaluron"            element={<Hijaluron />} />
                      <Route path="/blokada"              element={<Blokada />} />
                      <Route path="/udarni-val"           element={<UdarniVal />} />
                      <Route path="/dry-needling"         element={<DryNeedling />} />
                      <Route path="/limfna-drenaža"       element={<LimfnaDrenaza />} />
                      <Route path="/blog"                 element={<Blog2 />} />
                      <Route path="/cjenik"               element={<Cjenik />} />
                      <Route path="/politika-privatnosti" element={<PolitikaPrivatnosti />} />
                      <Route path="/kolacici"             element={<PravilaKolacici />} />
                      <Route path="/team/:id"             element={<TeamMemberDetail />} />
                    </Routes>
                  </Suspense>
                </PublicLayout>
              </SiteDataProvider>
            }
          />

        </Routes>
      </Router>
    </HelmetProvider>
  );
}

export default App;